import React, {useState, useEffect, useRef} from 'react';
import {Alert, Button, Card, Form, Input, Spin} from 'antd';
import {FormattedMessage, useIntl} from 'react-intl';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import {isMobileOnly} from 'react-device-detect';
import {CloseOutlined} from '@ant-design/icons';
import {getAuthenticationApi, getPublicApi} from '../api/apiInstances';
import {tokenService} from '../services/tokenService';
import '../assets/css/Dentobook.css';
import TurnstileWidget, {TurnstileHandle, TURNSTILE_ENABLED} from '../components/common/TurnstileWidget';

const AcceptInvitePage = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [form] = Form.useForm();

    const token = searchParams.get('token') ?? '';

    type InviteState = 'loading' | 'invalid' | 'existing_user' | 'new_user' | 'success';
    const [pageState, setPageState] = useState<InviteState>('loading');
    const [errMsg, setErrMsg] = useState('');
    const [orgName, setOrgName] = useState('');
    const [maskedEmail, setMaskedEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const turnstileRef = useRef<TurnstileHandle>(null);
    const submittingRef = useRef(false);

    useEffect(() => {
        if (!token) {
            setErrMsg(intl.formatMessage({id: 'invite_invalid'}));
            setPageState('invalid');
            return;
        }

        (async () => {
            try {
                const resp = await getPublicApi().getStaffInviteInfo(token);
                const info = resp.data;
                setOrgName(info.orgName);
                setMaskedEmail(info.maskedEmail);

                if (info.status === 'expired') {
                    setErrMsg(intl.formatMessage({id: 'invite_expired'}));
                    setPageState('invalid');
                } else if (info.status === 'revoked') {
                    setErrMsg(intl.formatMessage({id: 'invite_revoked'}));
                    setPageState('invalid');
                } else if (info.status === 'accepted') {
                    setErrMsg(intl.formatMessage({id: 'invite_already_accepted'}));
                    setPageState('invalid');
                } else if (info.status === 'pending') {
                    setPageState(info.isNewUser ? 'new_user' : 'existing_user');
                } else {
                    setErrMsg(intl.formatMessage({id: 'invite_invalid'}));
                    setPageState('invalid');
                }
            } catch {
                setErrMsg(intl.formatMessage({id: 'invite_invalid'}));
                setPageState('invalid');
            }
        })();
    }, [token, intl]);

    const validatePassword = (_: unknown, value: string) => {
        if (!value) return Promise.reject(intl.formatMessage({id: 'new_password_required'}));
        if (value.length < 9) return Promise.reject(intl.formatMessage({id: 'password_min_length_error'}));
        if (!/[0-9]/.test(value)) return Promise.reject(intl.formatMessage({id: 'password_must_contain_number'}));
        if (!/[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'/`~]/.test(value)) return Promise.reject(intl.formatMessage({id: 'password_must_contain_special'}));
        if (!/[A-Z]/.test(value)) return Promise.reject(intl.formatMessage({id: 'password_must_contain_uppercase'}));
        if (!/[a-z]/.test(value)) return Promise.reject(intl.formatMessage({id: 'password_must_contain_lowercase'}));
        return Promise.resolve();
    };

    const validateConfirmPassword = (_: unknown, value: string) => {
        const pw = form.getFieldValue('password');
        if (!value) return Promise.reject(intl.formatMessage({id: 'confirm_password_required'}));
        if (value !== pw) return Promise.reject(intl.formatMessage({id: 'passwords_do_not_match'}));
        return Promise.resolve();
    };

    const handleAccept = async (values?: {firstName?: string; lastName?: string; password?: string}) => {
        if (submittingRef.current) {
            return;
        }

        if (TURNSTILE_ENABLED && !turnstileToken) {
            setErrMsg(intl.formatMessage({id: 'complete_bot_verification'}));
            return;
        }

        submittingRef.current = true;
        setIsSubmitting(true);
        setErrMsg('');

        const capturedToken = turnstileToken;
        setTurnstileToken(null);

        try {
            const resp = await getPublicApi().redeemStaffInvite({
                token,
                ...(values?.password ? {password: values.password} : {}),
                ...(values?.firstName ? {firstName: values.firstName} : {}),
                ...(values?.lastName ? {lastName: values.lastName} : {}),
                turnstileToken: capturedToken || undefined,
            });
            const {organizationId} = resp.data;
            setPageState('success');

            if (tokenService.isAuthenticated() && organizationId) {
                // Already logged in — auto-switch into the newly joined org, no re-login needed.
                try {
                    const switchResp = await getAuthenticationApi().selectOrganization({organizationId});
                    const rememberMe = localStorage.getItem('dentobook_remember_me') === 'true';
                    tokenService.setTokens(switchResp.data.access_token!, switchResp.data.refresh_token!, rememberMe);
                    window.location.href = '/';
                } catch {
                    // Token swap failed — stay on page, show fallback message.
                    setErrMsg(intl.formatMessage({id: 'invite_switch_failed'}));
                }
            } else if (resp.data.accessToken && resp.data.refreshToken) {
                // Server minted fresh tokens in the redeem response — full reload so Authentication
                // re-runs fetchPrincipal with the new tokens (navigate() won't remount it).
                tokenService.setTokens(resp.data.accessToken, resp.data.refreshToken);
                window.location.href = '/';
            } else {
                // Fallback: no tokens (should not happen with current backend) — redirect to login.
                setTimeout(() => navigate('/login'), 2000);
            }
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            if (typeof detail === 'string') {
                if (detail.includes('expired')) setErrMsg(intl.formatMessage({id: 'invite_expired'}));
                else if (detail.includes('revoked')) setErrMsg(intl.formatMessage({id: 'invite_revoked'}));
                else if (detail.includes('accepted')) setErrMsg(intl.formatMessage({id: 'invite_already_accepted'}));
                else setErrMsg(detail);
            } else {
                setErrMsg(intl.formatMessage({id: 'unexpected_error'}));
            }
            turnstileRef.current?.reset();
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const cardStyle: React.CSSProperties = {
        width: isMobileOnly ? '90vw' : 480,
        padding: '40px',
        borderRadius: '30px',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh'}}>
            <div style={{marginBottom: '20px', textAlign: 'center'}}>
                <a href="https://dentobook.com/en/" target="_blank" rel="noopener noreferrer">
                    <img
                        alt="logo"
                        className="dentobook-logo"
                        src={require('../assets/logo.png')}
                        style={{width: '420px', height: '104px', padding: '13px 10px 13.65px 10px'}}
                    />
                </a>
            </div>

            <Card style={cardStyle}>
                <div style={{marginBottom: '24px', textAlign: 'center'}}>
                    <h2 style={{marginBottom: '8px', fontSize: '24px', fontWeight: 600}}>
                        <FormattedMessage id="accept_invite_title"/>
                    </h2>
                    {orgName && (
                        <p style={{color: '#666', fontSize: '14px', margin: 0}}>
                            <FormattedMessage id="accept_invite_description"/> <strong>{orgName}</strong>
                        </p>
                    )}
                </div>

                {pageState === 'loading' && (
                    <div style={{textAlign: 'center', padding: '40px 0'}}>
                        <Spin size="large"/>
                    </div>
                )}

                {pageState === 'success' && (
                    <>
                        <Alert
                            message={intl.formatMessage({id: 'invite_accepted'})}
                            type="success"
                            showIcon
                        />
                        {errMsg && (
                            <>
                                <Alert message={errMsg} type="warning" showIcon style={{marginTop: 16}}/>
                                <div style={{textAlign: 'center', marginTop: '16px'}}>
                                    <Link to="/" style={{color: '#1890ff', fontWeight: 500}}>
                                        <FormattedMessage id="dashboard"/>
                                    </Link>
                                </div>
                            </>
                        )}
                    </>
                )}

                {pageState === 'invalid' && (
                    <>
                        <Alert message={errMsg} type="error" showIcon className="mb-24"/>
                        <div style={{textAlign: 'center', marginTop: '16px'}}>
                            <Link to="/login" style={{color: '#1890ff', fontWeight: 500}}>
                                <FormattedMessage id="back_to_login"/>
                            </Link>
                        </div>
                    </>
                )}

                {pageState === 'existing_user' && (
                    <>
                        {errMsg && (
                            <Alert
                                message={errMsg}
                                type="error"
                                className="mb-24"
                                closeIcon={<CloseOutlined/>}
                                onClose={() => setErrMsg('')}
                                showIcon
                            />
                        )}
                        {maskedEmail && (
                            <p style={{color: '#555', marginBottom: '16px'}}>
                                {maskedEmail}
                            </p>
                        )}
                        <TurnstileWidget
                            ref={turnstileRef}
                            onVerify={setTurnstileToken}
                            onExpire={() => setTurnstileToken(null)}
                        />
                        <Button
                            type="primary"
                            size="large"
                            shape="round"
                            loading={isSubmitting}
                            disabled={TURNSTILE_ENABLED && !turnstileToken}
                            onClick={() => handleAccept()}
                            style={{width: '100%'}}
                        >
                            <FormattedMessage id="accept_invite_title"/>
                        </Button>
                        <div style={{textAlign: 'center', marginTop: '16px'}}>
                            <Link to="/login" style={{color: '#1890ff', fontWeight: 500}}>
                                <FormattedMessage id="back_to_login"/>
                            </Link>
                        </div>
                    </>
                )}

                {pageState === 'new_user' && (
                    <Form form={form} layout="vertical" onFinish={handleAccept} style={{textAlign: 'left'}}>
                        {errMsg && (
                            <Alert
                                message={errMsg}
                                type="error"
                                className="mb-24"
                                closeIcon={<CloseOutlined/>}
                                onClose={() => setErrMsg('')}
                                showIcon
                            />
                        )}
                        {maskedEmail && (
                            <p style={{color: '#555', marginBottom: '16px'}}>
                                {maskedEmail}
                            </p>
                        )}
                        <Form.Item
                            className="dentobook-form-item dentobook-simple-input"
                            label={<FormattedMessage id="first_name"/>}
                            name="firstName"
                            rules={[{required: true, message: intl.formatMessage({id: 'first_name_required'})}]}
                        >
                            <Input size="large" style={{borderRadius: '12px', height: '48px'}} autoComplete="given-name"/>
                        </Form.Item>
                        <Form.Item
                            className="dentobook-form-item dentobook-simple-input"
                            label={<FormattedMessage id="last_name"/>}
                            name="lastName"
                            rules={[{required: true, message: intl.formatMessage({id: 'last_name_required'})}]}
                        >
                            <Input size="large" style={{borderRadius: '12px', height: '48px'}} autoComplete="family-name"/>
                        </Form.Item>
                        <Form.Item
                            className="dentobook-form-item dentobook-simple-input"
                            label={<FormattedMessage id="new_password"/>}
                            name="password"
                            rules={[{validator: validatePassword}]}
                            extra={<div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}><FormattedMessage id="password_requirements_hint"/></div>}
                        >
                            <Input type="password" size="large" style={{borderRadius: '12px', height: '48px'}} autoComplete="new-password"/>
                        </Form.Item>
                        <Form.Item
                            className="dentobook-form-item dentobook-simple-input"
                            label={<FormattedMessage id="confirm_new_password"/>}
                            name="confirmPassword"
                            dependencies={['password']}
                            rules={[{validator: validateConfirmPassword}]}
                        >
                            <Input type="password" size="large" style={{borderRadius: '12px', height: '48px'}} autoComplete="new-password"/>
                        </Form.Item>
                        <TurnstileWidget
                            ref={turnstileRef}
                            onVerify={setTurnstileToken}
                            onExpire={() => setTurnstileToken(null)}
                        />
                        <Form.Item className="dentobook-form-item">
                            <Button
                                className="dentobook-btn-primary dentobook-btn-medium"
                                type="primary"
                                htmlType="submit"
                                loading={isSubmitting}
                                disabled={TURNSTILE_ENABLED && !turnstileToken}
                                style={{width: '100%', height: '48px'}}
                            >
                                <FormattedMessage id="create_account_and_accept"/>
                            </Button>
                        </Form.Item>
                        <div style={{textAlign: 'center', marginTop: '16px'}}>
                            <Link to="/login" style={{color: '#1890ff', fontWeight: 500}}>
                                <FormattedMessage id="back_to_login"/>
                            </Link>
                        </div>
                    </Form>
                )}
            </Card>
        </div>
    );
};

export default AcceptInvitePage;
